import { useState, useEffect, useCallback } from 'react';
import { reviewsCacheService } from '../services/ReviewsCacheService';
import { getCountUnreadReviewsFromData } from '../services/SocialNetworkService';

const globalCache = new Map<string, { count: number; timestamp: number }>();
const CACHE_TTL = 120000;

const eventEmitter = new EventTarget();

export const updateGlobalUnreadCount = (userId: string, count: number) => {
    globalCache.set(userId, { count, timestamp: Date.now() });
    localStorage.setItem('countUnreadReview', count.toString());
    eventEmitter.dispatchEvent(
        new CustomEvent('unread-count-updated', {
            detail: { userId, count },
        }),
    );
};

export const useGlobalUnreadCount = (userId: string | null, isSsoUser: boolean): number => {
    const [count, setCount] = useState(() => {
        return +(localStorage.getItem('countUnreadReview') ?? '0');
    });

    const updateCount = useCallback(async (userId: string) => {
        try {
            const cached = globalCache.get(userId);
            const now = Date.now();

            if (cached && now - cached.timestamp < CACHE_TTL) {
                setCount(cached.count);
                return;
            }

            const cacheInfo = reviewsCacheService.getCacheInfo(userId);
            if (cacheInfo && cacheInfo.hasInitialData && cacheInfo.reviews.length > 0) {
                const calculatedCount = getCountUnreadReviewsFromData(cacheInfo.reviews);
                updateGlobalUnreadCount(userId, calculatedCount);
                setCount(calculatedCount);
                return;
            }

            const { reviews } = await reviewsCacheService.getReviews(userId, false);

            if (reviews.length > 0) {
                const calculatedCount = getCountUnreadReviewsFromData(reviews);
                updateGlobalUnreadCount(userId, calculatedCount);
                setCount(calculatedCount);
            }
        } catch (error) {
            console.error('[useGlobalUnreadCount] Erreur lors de la mise à jour du count:', error);
        }
    }, []);

    useEffect(() => {
        if (!isSsoUser || !userId) {
            setCount(0);
            return;
        }

        updateCount(userId);
    }, [userId, isSsoUser, updateCount]);

    useEffect(() => {
        if (!userId) return;

        const handleCountUpdated = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { userId: updatedUserId, count: newCount } = customEvent.detail;

            if (updatedUserId === userId) {
                setCount(newCount);
            }
        };

        const handleCountInvalidated = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { userId: invalidatedUserId } = customEvent.detail;

            if (invalidatedUserId === userId || invalidatedUserId === 'all') {
                updateCount(userId);
            }
        };

        eventEmitter.addEventListener('unread-count-updated', handleCountUpdated);
        eventEmitter.addEventListener('unread-count-invalidated', handleCountInvalidated);

        return () => {
            eventEmitter.removeEventListener('unread-count-updated', handleCountUpdated);
            eventEmitter.removeEventListener('unread-count-invalidated', handleCountInvalidated);
        };
    }, [userId, updateCount]);

    return count;
};
